import { useContext, useState, useEffect, type ReactNode } from "react";
import {
  callCreateOrder,
  callFetchOrders,
  callUpdateOrderStatus,
  type Order,
} from "../../../api/orders";
import { OrderContext, type OrderContextType } from "./OrderContext";
import { HarvestContext } from "./HarvestContext";
import type { CartItem, DeliveryMethod } from "../utils/types";

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderContextProvider = ({ children }: OrderProviderProps) => {
  const { refreshHarvestProducts } = useContext(HarvestContext);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await callFetchOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (
    companyRegistrationNumber: string,
    products: CartItem[],
    deliveryMethod: DeliveryMethod,
    totalPrice: number,
  ): Promise<Order | null> => {
    setLoading(true);
    setError(null);
    try {
      const created = await callCreateOrder({
        companyRegistrationNumber,
        totalPrice,
        deliveryMethod,
        products,
      });
      setOrders((prev) => [...prev, created]);
      await refreshHarvestProducts();
      return created;
    } catch (err) {
      const axiosMessage =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data &&
        typeof err.response.data.message === "string"
          ? err.response.data.message
          : null;
      const errorMessage =
        axiosMessage ??
        (err instanceof Error ? err.message : "Failed to add order");
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refreshOrders = async (): Promise<void> => {
    await fetchOrders();
  };

  const updateStatus = async (id: string, newStatus: string) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await callUpdateOrderStatus(id, newStatus);
      if (newStatus === "Canceled") {
        await refreshHarvestProducts();
      }
      setOrders((prev) =>
        prev.map((order) =>
          order.id === id
            ? {
                ...order,
                status: updated.status,
                updatedAt: updated.updatedAt,
              }
            : order,
        ),
      );
      return updated;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update order status.";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const value: OrderContextType = {
    orders,
    loading,
    error,
    fetchOrders,
    createOrder,
    updateStatus,
    refreshOrders,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};
